import { Component, useCallback, useEffect, useRef, useState } from 'react';

import { AlertFullWidth } from '@/lib/base/overlays/alerts';
import { fetchWithDefaults } from '@/lib/http/client';

interface Banner {
  id?: number;
  title: string;
  message: string;
  severity: string;
  link_url?: string;
  link_text?: string;
  source?: string;
  updated_at: string;
}

const SEVERITY_TO_COLOR = {
  info: 'default',
  warning: 'warning',
  error: 'error',
  success: 'success',
} as const;

const POLL_INTERVAL_MS = 300_000; // 5 minutes (aligned with backend Cache-Control max-age=300)
const MAX_BANNERS = 10;

interface ErrorBoundaryProps {
  children: React.ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

class SystemStatusBannerErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error(
      'SystemStatusBanner error boundary caught:',
      error,
      errorInfo
    );
    this.setState({ hasError: true });
  }

  render() {
    if (this.state.hasError) {
      return null;
    }
    return this.props.children;
  }
}

function SystemStatusBannerInner() {
  const [banners, setBanners] = useState<Banner[]>([]);
  const [dismissedKeys, setDismissedKeys] = useState<Set<string>>(new Set());
  const controllerRef = useRef<AbortController | null>(null);
  const isMountedRef = useRef(true);

  // Load dismissed banners from sessionStorage on mount
  useEffect(() => {
    try {
      const stored = sessionStorage.getItem('dismissed_banners');
      const dismissed = JSON.parse(stored || '[]');
      if (Array.isArray(dismissed)) {
        setDismissedKeys(new Set(dismissed));
      }
    } catch (error) {
      console.warn(
        'Failed to parse dismissed banners from sessionStorage:',
        error
      );
    }
  }, []);

  const getBannerKey = useCallback((banner: Banner): string => {
    if (banner.id !== undefined && banner.id !== null) {
      // Include updated_at to make key change when content changes
      return `${banner.id}-${banner.updated_at}`;
    }
    return JSON.stringify({
      t: banner.title,
      s: banner.severity,
      u: banner.updated_at,
    });
  }, []);

  const fetchBanners = useCallback(async () => {
    controllerRef.current?.abort();
    const controller = new AbortController();
    controllerRef.current = controller;

    const timeoutId = setTimeout(() => controller.abort(), 5000);

    try {
      const response = await fetchWithDefaults('/api/system/banners/', {
        signal: controller.signal,
      });
      clearTimeout(timeoutId);

      if (!response.ok) {
        console.error('Failed to fetch system banners: HTTP', response.status);
        return;
      }

      let data;
      try {
        data = await response.json();
      } catch (parseError) {
        console.error('Failed to parse banner response JSON:', parseError);
        return;
      }

      if (!Array.isArray(data?.banners)) {
        console.warn('Invalid banner response shape:', data);
        return;
      }

      if (controller.signal.aborted) {
        return;
      }

      if (isMountedRef.current) {
        setBanners(data.banners.slice(0, MAX_BANNERS));
      }
    } catch (error) {
      if (controller.signal.aborted) {
        return;
      }
      console.error('Failed to fetch system banners:', error);
    }
  }, []);

  useEffect(() => {
    isMountedRef.current = true;

    fetchBanners();

    const intervalId = setInterval(fetchBanners, POLL_INTERVAL_MS);

    const handleFocus = () => {
      if (isMountedRef.current) {
        fetchBanners();
      }
    };
    window.addEventListener('focus', handleFocus);

    return () => {
      isMountedRef.current = false;
      clearInterval(intervalId);
      controllerRef.current?.abort();
      window.removeEventListener('focus', handleFocus);
    };
  }, [fetchBanners]);

  const handleDismiss = useCallback(
    (banner: Banner) => {
      const key = getBannerKey(banner);
      const newSet = new Set(dismissedKeys);
      newSet.add(key);
      setDismissedKeys(newSet);

      try {
        sessionStorage.setItem(
          'dismissed_banners',
          JSON.stringify([...newSet])
        );
      } catch (error) {
        console.warn(
          'Failed to save dismissed banners to sessionStorage:',
          error
        );
      }
    },
    [dismissedKeys, getBannerKey]
  );

  const visibleBanners = banners.filter(
    banner => !dismissedKeys.has(getBannerKey(banner))
  );

  if (visibleBanners.length === 0) {
    return null;
  }

  return (
    <div>
      {visibleBanners.map((banner, _index) => {
        const severityKey = banner.severity as keyof typeof SEVERITY_TO_COLOR;
        const color = SEVERITY_TO_COLOR[severityKey] ?? 'default';

        if (!(severityKey in SEVERITY_TO_COLOR)) {
          console.warn(
            `Unknown banner severity "${banner.severity}", using "default"`
          );
        }

        return (
          <AlertFullWidth
            key={getBannerKey(banner)}
            title={banner.title}
            description={banner.message}
            color={color}
            stackContent={true}
            onClose={() => handleDismiss(banner)}
            confirmLabel={banner.link_text}
            onConfirm={
              banner.link_url && banner.link_text
                ? () =>
                    window.open(
                      banner.link_url,
                      '_blank',
                      'noopener,noreferrer'
                    )
                : undefined
            }
            actionType={
              banner.link_url && banner.link_text ? 'button' : undefined
            }
          />
        );
      })}
    </div>
  );
}

export default function SystemStatusBanner() {
  return (
    <SystemStatusBannerErrorBoundary>
      <SystemStatusBannerInner />
    </SystemStatusBannerErrorBoundary>
  );
}
